// src/pages/IssueReporting/components/ImagePreviewDialog.js
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button
} from '@mui/material';
import { Image as ImageIcon } from 'react-feather';

/**
 * Component for displaying image previews in a dialog
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {Object} props.image - The image data object
 * @param {string} props.image.fileName - Name of the file
 * @param {string} props.image.thumbnail - Base64 encoded thumbnail
 * @param {string} props.image.fullPath - Full path to the image on server
 * @param {string} props.apiDetails - Base API URL for constructing download links
 */
const ImagePreviewDialog = ({ open, onClose, image, apiDetails }) => {
    if (!image) return null;

    // Construct the download URL
    const downloadUrl = `${apiDetails}pegasus/visionary/tickets/attachment/download?file_path=${encodeURIComponent(image.fullPath)}`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{py: 2, px: 3, backgroundColor: '#f8fafc'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        {image.fileName}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: '#f8fafc' }}>
                {image.thumbnail ? (
                    <img
                        src={`data:image/jpeg;base64,${image.thumbnail}`}
                        alt={image.fileName}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '70vh',
                            objectFit: 'contain',
                            padding: '16px'
                        }}
                    />
                ) : (
                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ImageIcon size={48} color="#9CA3AF" />
                        <Typography sx={{ mt: 2 }}>
                            Preview not available
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{py: 2, px: 3, borderTop: '1px solid #e2e8f0'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none'
                    }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        boxShadow: 'none'
                    }}
                    component="a"
                    href={downloadUrl}
                    target="_blank"
                    download
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImagePreviewDialog;